import { PermissionWorld, WorldAccessLevel, WorldLevels } from '@/models/out/FundPersonDtoOut';

/**
 * Permission worlds on the screens (Druk, 22.10.2026). The server holds every endpoint to them
 * (PermissionWorldFilter); this side hides what is closed and locks what may only be seen, so a
 * coordinator meets the limit before acting rather than after.
 */

export const WORLDS: PermissionWorld[] = ['Donations', 'Supports', 'Other'];

/** Everything at edit: a lead coordinator, everyone from before the worlds, and the default while the answer loads. */
export const FULL_WORLDS: WorldLevels = { donations: 'Edit', supports: 'Edit', other: 'Edit' };

const RANK: Record<WorldAccessLevel, number> = { None: 0, View: 1, Edit: 2 };

const keyOf = (world: PermissionWorld): keyof WorldLevels => (world === 'Donations' ? 'donations' : world === 'Supports' ? 'supports' : 'other');

export const levelIn = (worlds: WorldLevels, world: PermissionWorld): WorldAccessLevel => worlds[keyOf(world)];

export const atLeast = (level: WorldAccessLevel, needed: WorldAccessLevel) => RANK[level] >= RANK[needed];

export const withLevel = (worlds: WorldLevels, world: PermissionWorld, level: WorldAccessLevel): WorldLevels => ({
  ...worlds,
  [keyOf(world)]: level,
});

export const allAt = (level: WorldAccessLevel): WorldLevels => ({ donations: level, supports: level, other: level });

export const sameWorlds = (a: WorldLevels, b: WorldLevels) => WORLDS.every((w) => levelIn(a, w) === levelIn(b, w));

/** Full, view everywhere, or a mix - how a person's permission is named in one word. */
export const worldsSummary = (worlds: WorldLevels): 'full' | 'viewOnly' | 'custom' =>
  sameWorlds(worlds, FULL_WORLDS) ? 'full' : sameWorlds(worlds, allAt('View')) ? 'viewOnly' : 'custom';

/**
 * Which world a screen of the fund belongs to, by its path inside the fund (with or without the
 * fund id in front). Null for what every coordinator shares: the transactions board, the
 * dashboard, help - those narrow their own content.
 */
export const worldOfPath = (to: string): PermissionWorld | null => {
  const path = to.replace(/^\/+/, '').replace(/^\d+(\/|$)/, '');
  if (/^(standing-orders|bank-standing-orders|donations|addons)(\/|$)/.test(path)) return 'Donations';
  if (/^support\/inquiries(\/|$)/.test(path)) return 'Other';
  if (/^support(\/|$)/.test(path)) return 'Supports';
  if (/^fund-settings(\/|$)/.test(path)) return 'Other';
  return null;
};

/** A screen of the fund, by its path: open unless its world is closed to these worlds. */
export const canOpenIn = (worlds: WorldLevels, to: string) => {
  const world = worldOfPath(to);
  return world == null || atLeast(levelIn(worlds, world), 'View');
};

/** The older single level these worlds come down to, as the server computes it: full when editing anywhere. */
export const summaryLevel = (worlds: WorldLevels): 'Full' | 'ViewOnly' => (WORLDS.some((w) => levelIn(worlds, w) === 'Edit') ? 'Full' : 'ViewOnly');

/** A person's worlds, or what their single level gives when an older answer carries none. */
export const worldsOf = (person: { worlds?: WorldLevels | null; permissionLevel: string }): WorldLevels =>
  person.worlds ?? (person.permissionLevel === 'ViewOnly' ? allAt('View') : FULL_WORLDS);

/** «תרומות: עריכה · תמיכות: צפייה · שאר המערכת: ללא» - a mix, spelled out. */
export const describeWorlds = (worlds: WorldLevels, t: (key: string) => string) =>
  WORLDS.map((w) => `${t(`permissionWorlds.names.${w}`)}: ${t(`permissionWorlds.levels.${levelIn(worlds, w)}`)}`).join(' · ');

/** The server's record of a change in the settings history: "Full", "ViewOnly", or "Worlds:Edit,View,None". */
export const parseWorldsRecord = (value?: string | null): WorldLevels | null => {
  const match = value?.match(/^Worlds:(None|View|Edit),(None|View|Edit),(None|View|Edit)$/);
  if (!match) return null;
  return { donations: match[1] as WorldAccessLevel, supports: match[2] as WorldAccessLevel, other: match[3] as WorldAccessLevel };
};
